import { endOfYear, startOfYear } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown, Crosshair, Download, FileText, Search, User, Users, X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import type { DateRange } from '@daypicker/react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { DateRangeCalendar, type DateRangePreset } from '@/components/ui/DateRangePicker';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import { cn } from '@/lib/utils';
import { formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';
import { downloadChronologyExcel, downloadChronologyWord } from './export';
import { getCategoryStyle } from './styles';
import type { ChronologyOption, TypeEntry } from './useChronologyFilters';

const COUNT_SLOT_CLASSNAME = 'inline-block min-w-[2ch] shrink-0 text-right tabular-nums';
const COMPACT_FILTER_PILL_LIMIT = 4;
const FILTER_PILL_GRID_STYLE = {
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(16rem, 100%), 16rem))',
};
const FILTER_PILL_BUTTON_CLASSNAME =
  'inline-flex max-w-[18rem] items-center rounded-md border text-xs font-normal cursor-pointer select-none transition-[background-color,color,opacity,box-shadow] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-300/45 focus-visible:ring-offset-1';
const FILTER_PILL_LABEL_CLASSNAME = 'min-w-0 whitespace-normal break-words text-left leading-4';
const OTHER_FILTER_TRIGGER_CLASSNAME =
  'h-8 justify-start gap-1.5 rounded-md border-transparent bg-transparent px-2.5 text-xs font-normal shadow-none transition-colors hover:bg-gray-50/80 hover:text-gray-700';
const OTHER_FILTER_ACTIVE_CLASSNAME = 'bg-gray-50/70 text-gray-700 hover:bg-gray-100/70';
const OTHER_FILTER_INACTIVE_CLASSNAME = 'text-gray-400';
const OTHER_FILTER_BADGE_CLASSNAME =
  'ml-0.5 inline-flex min-w-[1rem] items-center justify-center rounded-full bg-gray-100/80 px-1 text-[10px] text-gray-500 tabular-nums';
const OTHER_FILTER_SEPARATOR_CLASSNAME = 'hidden';

function getFilterPillGridStyle(itemCount: number) {
  if (itemCount > 0 && itemCount <= COMPACT_FILTER_PILL_LIMIT) {
    return { gridTemplateColumns: `repeat(${itemCount}, max-content)` };
  }
  return FILTER_PILL_GRID_STYLE;
}

function exportErrorMessage(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

interface SearchableSelectionPopoverProps {
  triggerLabel: string;
  icon: ReactNode;
  counts: ReadonlyMap<string, number>;
  activeValues: ReadonlySet<string>;
  searchPlaceholder: string;
  widthClassName: string;
  onToggle: (value: string) => void;
  onClear: () => void;
  sortEntries?: (a: [string, number], b: [string, number]) => number;
}

function SearchableSelectionPopover({
  triggerLabel,
  icon,
  counts,
  activeValues,
  searchPlaceholder,
  widthClassName,
  onToggle,
  onClear,
  sortEntries,
}: SearchableSelectionPopoverProps) {
  const [search, setSearch] = useState('');

  return (
    <Popover onOpenChange={open => { if (!open) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            OTHER_FILTER_TRIGGER_CLASSNAME,
            activeValues.size > 0 ? OTHER_FILTER_ACTIVE_CLASSNAME : OTHER_FILTER_INACTIVE_CLASSNAME,
          )}
        >
          {icon}
          {triggerLabel}
          {activeValues.size > 0 && (
            <span className={OTHER_FILTER_BADGE_CLASSNAME}>{activeValues.size}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn(widthClassName, 'p-0')} align="start" side="bottom" sideOffset={4}>
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded-sm bg-white focus:outline-hidden focus:ring-1 focus:ring-gray-300 placeholder:text-gray-400"
            />
          </div>
        </div>
        <div className="max-h-[240px] overflow-y-auto py-1">
          {Array.from(counts.entries())
            .sort(sortEntries ?? ((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])))
            .filter(([name]) => !search.trim() || name.toLowerCase().includes(search.toLowerCase().trim()))
            .map(([name, count]) => {
              const active = activeValues.has(name);
              return (
                <button
                  type="button"
                  key={name}
                  onClick={() => onToggle(name)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 transition-colors',
                    active && 'bg-gray-50',
                  )}
                >
                  <span className={cn(
                    'w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 transition-colors',
                    active ? 'bg-gray-600 border-gray-600' : 'border-gray-300',
                  )}>
                    {active && <span className="text-white text-[9px] leading-none">&#10003;</span>}
                  </span>
                  <span className="truncate flex-1 text-gray-700">{name}</span>
                  <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px] text-gray-400')}>{count}</span>
                </button>
              );
            })}
        </div>
        {activeValues.size > 0 && (
          <div className="border-t border-gray-100 p-1.5">
            <button type="button" onClick={onClear} className="w-full text-center text-[11px] text-gray-500 hover:text-gray-700 font-medium py-0.5">
              Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface CategoryTypePopoverProps {
  categoryName: string;
  items: TypeEntry[];
  excludedTypes: ReadonlySet<string>;
  activeCategories: ReadonlySet<string>;
  typeCounts: ReadonlyMap<string, number>;
  colorByCategory?: ReadonlyMap<string, string | null>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onToggleType: (typeKey: string, category: string) => void;
  onOnlyType: (typeKey: string, category: string) => void;
  onShowAllTypes: (typeKeys: readonly string[]) => void;
}

function CategoryTypePopover({
  categoryName,
  items,
  excludedTypes,
  activeCategories,
  typeCounts,
  colorByCategory,
  open,
  onOpenChange,
  onToggleType,
  onOnlyType,
  onShowAllTypes,
}: CategoryTypePopoverProps) {
  const [search, setSearch] = useState('');
  const categoryStyle = getCategoryStyle(categoryName, colorByCategory);
  const query = search.trim().toLowerCase();
  const filteredItems = query
    ? items.filter(item => item.label.toLowerCase().includes(query) || item.key.toLowerCase().includes(query))
    : items;

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        onOpenChange(nextOpen);
        if (!nextOpen) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          title={`Filter ${categoryName} tags`}
          aria-label={`Filter ${categoryName} tags`}
          className={cn(
            'inline-flex shrink-0 cursor-pointer items-center self-stretch pl-0 pr-1 py-0.5 text-inherit transition-opacity',
            open ? 'opacity-100' : 'opacity-70 hover:opacity-100',
          )}
        >
          <ChevronDown className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end" side="bottom" sideOffset={4}>
        <div className="px-3 pt-3 pb-2 border-b border-gray-100">
          <div className="min-w-0 flex items-center gap-1.5">
            <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', categoryStyle.activeDot)} />
            <p className="truncate text-xs font-semibold text-gray-700">{categoryName}</p>
          </div>
        </div>
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search tags..."
              className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded-sm bg-white focus:outline-hidden focus:ring-1 focus:ring-gray-300 placeholder:text-gray-400"
            />
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto scrollbar py-1">
          {filteredItems.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-400">No matching tags.</p>
          ) : (
            filteredItems.map(item => {
              const included = !excludedTypes.has(item.key);
              const categoryPasses = activeCategories.size === 0 || activeCategories.has(item.category);
              const count = typeCounts.get(item.key) ?? 0;
              return (
                <div
                  key={item.key}
                  className={cn(
                    'group flex w-full items-center px-2 text-xs hover:bg-gray-50 transition-colors',
                    !categoryPasses && 'opacity-60',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onToggleType(item.key, item.category)}
                    className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-1 text-left"
                  >
                    <span className={cn(
                      'w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors',
                      included ? 'bg-gray-600 border-gray-600' : 'border-gray-300',
                    )}>
                      {included && <span className="text-white text-[9px] leading-none">&#10003;</span>}
                    </span>
                    <span className={cn('truncate flex-1', included ? 'text-gray-700' : 'text-gray-400')}>
                      {item.label || item.key}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onOnlyType(item.key, item.category)}
                    title="Show only this tag"
                    aria-label={`Show only ${item.label || item.key}`}
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-gray-300"
                  >
                    <Crosshair className="h-3 w-3" />
                  </button>
                  <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px] text-gray-400')}>{count}</span>
                </div>
              );
            })
          )}
        </div>
        {items.some(item => excludedTypes.has(item.key)) && (
          <div className="border-t border-gray-100 p-1.5">
            <button
              type="button"
              onClick={() => onShowAllTypes(items.map(item => item.key))}
              className="w-full text-center text-[11px] text-gray-500 hover:text-gray-700 font-medium py-0.5"
            >
              Show all
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ExportPopoverProps {
  matterGuid?: string;
  chronologyId?: string | null;
  filteredEvents: ChronologyEventLight[];
  locale: RegionalDateLocale;
}

function ExportPopover({
  matterGuid,
  chronologyId = null,
  filteredEvents,
  locale,
}: ExportPopoverProps) {
  const detailId = useId();
  const sourcesId = useId();
  const sourceDocumentsTableId = useId();
  const crimsonLinksId = useId();
  const [open, setOpen] = useState(false);
  const [dateFormat, setDateFormat] = useState(locale === 'us' ? 'MMM d, yyyy' : 'd MMM yyyy');
  const [includeSources, setIncludeSources] = useState(true);
  const [includeDetail, setIncludeDetail] = useState(true);
  const [includeSourceDocumentsTable, setIncludeSourceDocumentsTable] = useState(false);
  const [includeCrimsonDocumentLinks, setIncludeCrimsonDocumentLinks] = useState(false);
  const [exporting, setExporting] = useState<'word' | 'excel' | null>(null);

  useEffect(() => {
    setDateFormat(locale === 'us' ? 'MMM d, yyyy' : 'd MMM yyyy');
  }, [locale]);

  const commonOptions = {
    matterGuid: matterGuid ?? '',
    chronologyId,
    filteredEvents,
    dateFormat,
    includeSources,
    includeDetail,
    includeSourceDocumentsTable,
    includeCrimsonDocumentLinks,
    onComplete: () => setOpen(false),
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Export chronology"
          className="shrink-0 rounded-md border border-transparent bg-transparent p-1.5 text-gray-400 transition-colors hover:bg-gray-50/80 hover:text-gray-600"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="end" side="bottom" sideOffset={4}>
        <div className="px-3 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-700">Export options</p>
          <p className="text-[11px] text-gray-500">{filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'}</p>
        </div>
        <div className="p-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] font-medium text-gray-500">Date format</Label>
            <Select value={dateFormat} onValueChange={setDateFormat}>
              <SelectTrigger
                tabIndex={-1}
                className="h-8 text-xs focus:ring-0 focus:ring-offset-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={locale === 'us' ? 'MMM d, yyyy' : 'd MMM yyyy'}>
                  {locale === 'us' ? 'Apr 25, 2026' : '25 Apr 2026'}
                </SelectItem>
                <SelectItem value={locale === 'us' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}>
                  {locale === 'us' ? '04/25/2026' : '25/04/2026'}
                </SelectItem>
                <SelectItem value="yyyy-MM-dd">2026-04-25</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={detailId}
              tabIndex={-1}
              checked={includeDetail}
              onCheckedChange={value => setIncludeDetail(value === true)}
            />
            <Label htmlFor={detailId} className="text-xs text-gray-600 cursor-pointer">Include detail column</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={sourcesId}
              tabIndex={-1}
              checked={includeSources}
              onCheckedChange={(value) => {
                const checked = value === true;
                setIncludeSources(checked);
                if (!checked) setIncludeCrimsonDocumentLinks(false);
              }}
            />
            <Label htmlFor={sourcesId} className="text-xs text-gray-600 cursor-pointer">Include sources column</Label>
          </div>
          {includeSources && (
            <div className="ml-6 flex items-center gap-2">
              <Checkbox
                id={crimsonLinksId}
                tabIndex={-1}
                checked={includeCrimsonDocumentLinks}
                onCheckedChange={value => setIncludeCrimsonDocumentLinks(value === true)}
              />
              <Label htmlFor={crimsonLinksId} className="text-xs text-gray-600 cursor-pointer">Include Crimson document links</Label>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id={sourceDocumentsTableId}
              tabIndex={-1}
              checked={includeSourceDocumentsTable}
              onCheckedChange={value => setIncludeSourceDocumentsTable(value === true)}
            />
            <Label htmlFor={sourceDocumentsTableId} className="text-xs text-gray-600 cursor-pointer">Include source documents table</Label>
          </div>
        </div>
        <div className="px-3 pb-3 flex gap-2">
          <Button
            onClick={async () => {
              if (!matterGuid) return;
              setExporting('word');
              try {
                await downloadChronologyWord(commonOptions);
              } catch (error) {
                toast.error(exportErrorMessage(error, 'Failed to export chronology as Word'));
              } finally {
                setExporting(null);
              }
            }}
            disabled={exporting !== null || !matterGuid}
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs gap-1.5"
          >
            <Download className="h-3 w-3" />
            {exporting === 'word' ? 'Preparing' : 'Word'}
          </Button>
          <Button
            onClick={async () => {
              if (!matterGuid) return;
              setExporting('excel');
              try {
                await downloadChronologyExcel(commonOptions);
              } catch (error) {
                toast.error(exportErrorMessage(error, 'Failed to export chronology as Excel'));
              } finally {
                setExporting(null);
              }
            }}
            disabled={exporting !== null || !matterGuid}
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs gap-1.5"
          >
            <Download className="h-3 w-3" />
            {exporting === 'excel' ? 'Preparing' : 'Excel'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface ChronologyFilterBarProps {
  events: ChronologyEventLight[];
  filteredEvents: ChronologyEventLight[];
  matterGuid?: string;
  chronologyId?: string | null;
  locale: RegionalDateLocale;
  colorByCategory?: ReadonlyMap<string, string | null>;
  isAggregate: boolean;
  showSingleChronologyInfo: boolean;
  chronologyOptions: ChronologyOption[];
  visibleCategoryGroups: string[];
  visibleTypesByCategory: ReadonlyMap<string, TypeEntry[]>;
  categoryCounts: ReadonlyMap<string, number>;
  typeCounts: ReadonlyMap<string, number>;
  entityEventCounts: ReadonlyMap<string, number>;
  creatorEventCounts: ReadonlyMap<string, number>;
  sourceEventCounts: ReadonlyMap<string, number>;
  eventYears: number[];
  activeCategories: ReadonlySet<string>;
  excludedTypes: ReadonlySet<string>;
  activeChronologies: ReadonlySet<string>;
  activeEntities: ReadonlySet<string>;
  activeCreators: ReadonlySet<string>;
  activeSources: ReadonlySet<string>;
  dateRange: DateRange | undefined;
  relevanceThreshold: number;
  onToggleChronology: (type: string) => void;
  onToggleCategory: (name: string) => void;
  onToggleType: (typeKey: string, category: string) => void;
  onOnlyType: (typeKey: string, category: string) => void;
  onToggleEntity: (name: string) => void;
  onToggleCreator: (name: string) => void;
  onToggleSource: (name: string) => void;
  onShowAllTypesInCategory: (typeKeys: readonly string[]) => void;
  onClearEntities: () => void;
  onClearCreators: () => void;
  onClearSources: () => void;
  onSetDateRange: (range: DateRange | undefined) => void;
  onSetRelevanceThreshold: (value: number) => void;
}

export function ChronologyFilterBar({
  events,
  filteredEvents,
  matterGuid,
  chronologyId = null,
  locale,
  colorByCategory,
  isAggregate,
  showSingleChronologyInfo,
  chronologyOptions,
  visibleCategoryGroups,
  visibleTypesByCategory,
  categoryCounts,
  typeCounts,
  entityEventCounts,
  creatorEventCounts,
  sourceEventCounts,
  eventYears,
  activeCategories,
  excludedTypes,
  activeChronologies,
  activeEntities,
  activeCreators,
  activeSources,
  dateRange,
  relevanceThreshold,
  onToggleChronology,
  onToggleCategory,
  onToggleType,
  onOnlyType,
  onToggleEntity,
  onToggleCreator,
  onToggleSource,
  onShowAllTypesInCategory,
  onClearEntities,
  onClearCreators,
  onClearSources,
  onSetDateRange,
  onSetRelevanceThreshold,
}: ChronologyFilterBarProps) {
  const [openTypeCategory, setOpenTypeCategory] = useState<string | null>(null);
  const dateRangePopoverWidthClassName = 'w-[calc(100vw-2rem)] max-w-[31rem]';
  const hasRelevanceScores = events.some(ev => ev.relevance_score != null);
  const typesByCategoryName = useMemo(() => {
    const map = new Map<string, TypeEntry[]>();
    for (const items of visibleTypesByCategory.values()) {
      for (const item of items) {
        const current = map.get(item.category);
        if (current) current.push(item);
        else map.set(item.category, [item]);
      }
    }
    return map;
  }, [visibleTypesByCategory]);
  const displayCategoryGroups = useMemo(() => {
    const categories = new Set(visibleCategoryGroups);
    for (const categoryName of typesByCategoryName.keys()) categories.add(categoryName);
    return Array.from(categories);
  }, [visibleCategoryGroups, typesByCategoryName]);

  const yearPresets = useMemo<DateRangePreset[]>(
    () => eventYears.map(year => {
      const date = new Date(year, 0, 1);
      return {
        id: String(year),
        label: String(year),
        range: { from: startOfYear(date), to: endOfYear(date) },
      };
    }),
    [eventYears],
  );

  useEffect(() => {
    if (!openTypeCategory) return;
    if (typesByCategoryName.has(openTypeCategory)) return;
    setOpenTypeCategory(null);
  }, [openTypeCategory, typesByCategoryName]);

  return (
    <div className="px-6 pt-5 pb-4 space-y-4 border-b border-gray-200 bg-white">
      {((isAggregate && chronologyOptions.length > 1) || showSingleChronologyInfo) && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Chronologies</p>
          <div
            className="grid max-w-[88rem] items-center justify-items-start gap-x-6 gap-y-2"
            style={getFilterPillGridStyle(chronologyOptions.length)}
          >
            {chronologyOptions.map(({ type, name, count }) => {
              const informationalSingle = showSingleChronologyInfo && chronologyOptions.length === 1;
              const active = informationalSingle || activeChronologies.has(type);
              if (informationalSingle) {
                return (
                  <div
                    key={type}
                    className="inline-flex max-w-[18rem] items-center gap-1 rounded border bg-stone-50/40 px-2 py-0.5 text-[11px] font-medium text-stone-600 border-stone-400/18"
                  >
                    <span className={FILTER_PILL_LABEL_CLASSNAME}>{name}</span>
                    <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px] opacity-80')}>{count}</span>
                  </div>
                );
              }
              return (
                <button
                  type="button"
                  key={type}
                  onClick={() => onToggleChronology(type)}
                  aria-pressed={active}
                  className={cn(
                    FILTER_PILL_BUTTON_CLASSNAME,
                    'gap-1.5 px-2 py-0.5',
                    active
                      ? 'bg-stone-50/40 border-stone-400/18 text-stone-600'
                      : 'border-transparent bg-gray-50/30 text-gray-500 hover:bg-gray-100/55 hover:text-gray-700',
                  )}
                >
                  <span className={FILTER_PILL_LABEL_CLASSNAME}>{name}</span>
                  <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px]', active ? 'opacity-80' : 'opacity-50')}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {displayCategoryGroups.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Tag categories</p>
          <div
            className="grid max-w-[88rem] items-center justify-items-start gap-x-6 gap-y-2"
            style={getFilterPillGridStyle(displayCategoryGroups.length)}
          >
            {displayCategoryGroups
              .slice()
              .map(name => {
                const count = categoryCounts.get(name) ?? 0;
                const style = getCategoryStyle(name, colorByCategory);
                const active = activeCategories.has(name);
                const categoryTypes = typesByCategoryName.get(name) ?? [];
                if (categoryTypes.length === 0) {
                  return (
                    <button
                      type="button"
                      key={name}
                      onClick={() => onToggleCategory(name)}
                      aria-pressed={active}
                      title={`Toggle ${name} category filter`}
                      className={cn(
                        FILTER_PILL_BUTTON_CLASSNAME,
                        'gap-1.5 px-2 py-0.5',
                        active ? style.activePill : style.pill,
                        active ? 'opacity-90' : 'opacity-85 hover:opacity-100',
                      )}
                    >
                      <span className={cn('w-1.5 h-1.5 rounded-full', active ? style.activeDot : style.dot)} />
                      <span className={FILTER_PILL_LABEL_CLASSNAME}>{name}</span>
                      <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px]', active ? 'opacity-80' : 'opacity-60')}>
                        {count}
                      </span>
                    </button>
                  );
                }

                return (
                  <div
                    key={name}
                    className={cn(
                      FILTER_PILL_BUTTON_CLASSNAME,
                      active ? style.activePill : style.pill,
                      active ? 'opacity-90' : 'opacity-85 hover:opacity-100',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onToggleCategory(name)}
                      aria-pressed={active}
                      title={`Toggle ${name} category filter`}
                      className="inline-flex min-w-0 cursor-pointer items-center gap-1.5 rounded-l-md py-0.5 pl-2 pr-0.5 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-300/45 focus-visible:ring-offset-1"
                    >
                      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', active ? style.activeDot : style.dot)} />
                      <span className={FILTER_PILL_LABEL_CLASSNAME}>{name}</span>
                      <span className={cn(COUNT_SLOT_CLASSNAME, 'text-[10px]', active ? 'opacity-80' : 'opacity-60')}>
                        {count}
                      </span>
                    </button>
                    <CategoryTypePopover
                      categoryName={name}
                      items={categoryTypes}
                      excludedTypes={excludedTypes}
                      activeCategories={activeCategories}
                      typeCounts={typeCounts}
                      colorByCategory={colorByCategory}
                      open={openTypeCategory === name}
                      onOpenChange={open => setOpenTypeCategory(current => (open ? name : current === name ? null : current))}
                      onToggleType={onToggleType}
                      onOnlyType={onOnlyType}
                      onShowAllTypes={onShowAllTypesInCategory}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Other filters</p>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  OTHER_FILTER_TRIGGER_CLASSNAME,
                  dateRange?.from || dateRange?.to ? OTHER_FILTER_ACTIVE_CLASSNAME : OTHER_FILTER_INACTIVE_CLASSNAME,
                )}
              >
                <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <span>{formatRegionalDate(dateRange.from, locale)} - {formatRegionalDate(dateRange.to, locale)}</span>
                  ) : (
                    <span>From {formatRegionalDate(dateRange.from, locale)}</span>
                  )
                ) : (
                  <span>Date range</span>
                )}
                {(dateRange?.from || dateRange?.to) && (
                  <button
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      onSetDateRange(undefined);
                    }}
                    className="ml-0.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit max-w-[calc(100vw-2rem)] p-0" align="start" side="bottom" sideOffset={8}>
              <div className={dateRangePopoverWidthClassName}>
                <DateRangeCalendar
                  value={dateRange}
                  onChange={onSetDateRange}
                  initialMonth={dateRange?.from ?? (eventYears.length > 0 ? new Date(eventYears[0], 0, 1) : undefined)}
                  customPresets={eventYears.length > 1 ? yearPresets : undefined}
                />
              </div>
            </PopoverContent>
          </Popover>

          {sourceEventCounts.size > 0 && (
            <>
              <div className={OTHER_FILTER_SEPARATOR_CLASSNAME} aria-hidden="true" />
              <SearchableSelectionPopover
                key="source-filter"
                triggerLabel="Source"
                icon={<FileText className="h-3.5 w-3.5 shrink-0" />}
                counts={sourceEventCounts}
                activeValues={activeSources}
                searchPlaceholder="Search sources..."
                widthClassName="w-[260px]"
                onToggle={onToggleSource}
                onClear={onClearSources}
              />
            </>
          )}

          {entityEventCounts.size > 0 && (
            <>
              <div className={OTHER_FILTER_SEPARATOR_CLASSNAME} aria-hidden="true" />
              <SearchableSelectionPopover
                key="entity-filter"
                triggerLabel="Entities"
                icon={<Users className="h-3.5 w-3.5 shrink-0" />}
                counts={entityEventCounts}
                activeValues={activeEntities}
                searchPlaceholder="Search entities..."
                widthClassName="w-[240px]"
                onToggle={onToggleEntity}
                onClear={onClearEntities}
                sortEntries={(a, b) => b[1] - a[1]}
              />
            </>
          )}

          {hasRelevanceScores && (
            <>
              <div className={OTHER_FILTER_SEPARATOR_CLASSNAME} aria-hidden="true" />
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-[11px] text-gray-500">Relevance</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={relevanceThreshold * 100}
                  onChange={event => onSetRelevanceThreshold(Number(event.target.value) / 100)}
                  className="w-24 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                />
                <span className="text-[10px] text-gray-400 w-6">
                  {relevanceThreshold > 0 ? `${Math.round(relevanceThreshold * 100)}%` : 'All'}
                </span>
              </div>
            </>
          )}

          {creatorEventCounts.size > 0 && (
            <>
              <div className={OTHER_FILTER_SEPARATOR_CLASSNAME} aria-hidden="true" />
              <SearchableSelectionPopover
                key="creator-filter"
                triggerLabel="Created by"
                icon={<User className="h-3.5 w-3.5 shrink-0" />}
                counts={creatorEventCounts}
                activeValues={activeCreators}
                searchPlaceholder="Search creators..."
                widthClassName="w-[220px]"
                onToggle={onToggleCreator}
                onClear={onClearCreators}
              />
            </>
          )}
          <div className="flex-1" />
          <ExportPopover
            matterGuid={matterGuid}
            chronologyId={chronologyId}
            filteredEvents={filteredEvents}
            locale={locale}
          />
        </div>
      </div>
    </div>
  );
}
